import { qs, vaultFetch } from './vault-api-core.ts'
import type { SealedChunk } from './vault-file-chunks.ts'

/**
 * The OPERATOR plane of vault file transfer:
 * `/vault/admin/{rootId}/files`, session cookie plus root membership.
 *
 * The server side is now in-tree: these shapes mirror
 * `app/control-api/src/routes/vault-file-schemas.ts` field for field, and
 * the paths mirror `vault-files-admin.ts`. That mirroring is the whole
 * contract — `vaultFetch<T>` is an unchecked generic, so nothing here is
 * validated at runtime and a field renamed on one side only would
 * typecheck on both. When you change one file, change the other.
 *
 * The underlying storage decision (D1/D3) is `vault_files` metadata plus
 * `vault_file_chunks`, 5 MiB total, 1 MiB chunks, filename sanitised
 * server-side, declared content type never trusted.
 *
 * A 404 is still tolerated rather than thrown, because the admin app and
 * the API deploy separately and an app that is briefly ahead of its API
 * should degrade, not break: the files screen renders a "not available
 * yet" callout instead of an error, and the notification watcher does not
 * poll files at all (D11 names two sources, and files is not one).
 *
 * CONFIDENTIALITY: chunks are sealed to the TENANT key, so any approved
 * daemon of this tenant can open any of this tenant's files. Recipient
 * scoping is authorisation, not cryptography; this is not end-to-end
 * encryption between two principals (D2), and a tenant-key rotation
 * makes existing files unreadable (D1).
 *
 * CONTENT TYPE IS METADATA FOR A HUMAN AND NOTHING ELSE. It is displayed
 * as text, and it must never reach an `<iframe>`, an `<img src>`, a
 * `Blob` that is navigated to, or any other decision about how to render
 * something. A stored `text/html` that got rendered would be stored XSS
 * against the one origin that holds the tenant key.
 */

export interface VaultFileSummary {
  id: string
  /** The name AS THE SERVER STORED IT, after its own sanitisation.
   * Still untrusted text — render it as a string child, never as
   * markup, and never use it to build a path. */
  filename: string
  /** Declared by the uploader. See the module docblock: display only. */
  contentType: string
  byteSize: number
  chunkCount: number
  /** How many ordinals have actually landed. `uploadedChunks <
   * chunkCount` is an upload still in flight (or abandoned): the bytes
   * are not all there and the file cannot be opened yet. The server
   * computes this per row, so it is always present on the wire. */
  uploadedChunks: number
  owner: { principalId: string; name: string | null }
  recipient: { principalId: string; name: string | null }
  sessionId: string | null
  /** Which tenant key sealed the chunks. A file sealed under an older
   * version cannot be opened by the current key. */
  kekVersion: number | null
  createdAt: string
  /** Never null: `vault_files.expires_at` is `NOT NULL` and every
   * transfer lapses on its own clock (D3). The housekeeping sweep prunes
   * rows past it, so a listed file may still be expired but never
   * undated. */
  expiresAt: string
  consumedAt: string | null
  [key: string]: unknown
}

export interface ListVaultFilesOptions {
  limit?: number
  /** A file id from the previous page (uuidv7 cursor). */
  before?: string
  /** Narrows to files this principal owns OR received. */
  principalId?: string
}

export async function listVaultFiles(
  rootId: string,
  options: ListVaultFilesOptions = {},
): Promise<VaultFileSummary[]> {
  const { files } = await vaultFetch<{ files: VaultFileSummary[] }>(
    `/vault/admin/${rootId}/files${qs({
      limit: options.limit?.toString(),
      before: options.before,
      principalId: options.principalId,
    })}`,
  )
  return files
}

export interface CreateVaultFileBody {
  recipientPrincipalId: string
  filename: string
  contentType: string
  byteSize: number
  /** Declared up front so the server can refuse an over-cap upload
   * before a single chunk is sent, and can tell a complete file from an
   * abandoned one. It is a DECLARATION: the server still counts the
   * bytes it actually receives (D3). */
  chunkCount: number
}

/** Creates the metadata row and returns the id every chunk is posted
 * against. Nothing is readable until all `chunkCount` chunks land. */
export function createVaultFile(
  rootId: string,
  body: CreateVaultFileBody,
): Promise<VaultFileSummary> {
  return vaultFetch<VaultFileSummary>(`/vault/admin/${rootId}/files`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * One chunk exactly as `VaultFileChunkOut` puts it on the wire. The
 * server echoes the stored row back from both the PUT and the by-ordinal
 * GET, and `SealedChunk` is a structural subset of it, so a value of
 * this type goes straight into `openAndJoinFile`.
 */
export interface VaultFileChunkOut extends SealedChunk {
  fileId: string
}

/**
 * Stores one sealed chunk. Idempotent on `(fileId, ordinal)`, so a
 * retried chunk replaces rather than duplicates.
 *
 * REVIEW FINDING: this used to post `{ciphertext, nonce, wrappedDek}`
 * and declare the reply as `{ok: true}`. Both were wrong against the
 * real route. `VaultFileChunkIn` requires `plainBytes`, so EVERY upload
 * 400ed on its first chunk — leaving an orphaned open transfer behind
 * each time, which counts against the per-principal cap until it
 * lapses. And the route answers with the stored chunk, not `{ok:true}`.
 */
export function putVaultFileChunk(
  rootId: string,
  fileId: string,
  chunk: SealedChunk,
): Promise<VaultFileChunkOut> {
  return vaultFetch<VaultFileChunkOut>(
    `/vault/admin/${rootId}/files/${fileId}/chunks/${String(chunk.ordinal)}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        ciphertext: chunk.ciphertext,
        nonce: chunk.nonce,
        wrappedDek: chunk.wrappedDek,
        plainBytes: chunk.plainBytes,
      }),
    },
  )
}

/**
 * Every sealed chunk of one file: the ordinal list, then one fetch per
 * ordinal.
 *
 * REVIEW FINDING: this used to read `{ chunks }` off
 * `GET …/files/{id}/chunks` in a single request. That route serves
 * `{ ordinals: number[] }` and says so in its own description — "Never
 * the bytes" — so `chunks` was always `undefined`, and the unchecked
 * `vaultFetch<T>` cast handed that straight to `openAndJoinFile` as a
 * `SealedChunk[]`. Every download from the console failed. The
 * component test mocks this function, so the suite stayed green.
 *
 * ONE REQUEST PER ORDINAL IS NOT A CHOICE MADE HERE. The server
 * deliberately has no route that returns several chunks' ciphertext at
 * once, because a list endpoint that can pull a whole file's bytes is
 * the thing it is avoiding. Sequential, for the same reason the upload
 * is: at a 5 MiB cap the wall-clock difference does not pay for five
 * 1 MiB ciphertexts in flight at once.
 *
 * The ordinal list is the server's own answer about what exists, so a
 * gap shows up here as a short array and `openAndJoinFile` turns it
 * into a named `missing_chunk` rather than a short file.
 */
export async function getVaultFileChunks(
  rootId: string,
  fileId: string,
): Promise<VaultFileChunkOut[]> {
  const { ordinals } = await vaultFetch<{ ordinals: number[] }>(
    `/vault/admin/${rootId}/files/${fileId}/chunks`,
  )
  const chunks: VaultFileChunkOut[] = []
  for (const ordinal of ordinals)
    chunks.push(
      await vaultFetch<VaultFileChunkOut>(
        `/vault/admin/${rootId}/files/${fileId}/chunks/${String(ordinal)}`,
      ),
    )
  return chunks
}

/*
 * There is deliberately NO `deleteVaultFile` here.
 *
 * An earlier draft of this module shipped one, calling
 * `DELETE /vault/admin/{rootId}/files/{id}` — a route that does not
 * exist on the server, is not in `vault-queries-files.ts`, and is not in
 * D3. Nothing called it, so nothing broke; it would simply have 404ed
 * the first time a button was wired to it. A transfer is retired by its
 * own clock (`expiresAt`, swept by housekeeping) or by the recipient
 * consuming it, and un-sending is not a modelled operation. If it ever
 * becomes one, it needs a route, a query and a decision record first.
 */
